import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { adminContent } from "@/admin/adminContent";
import { adminLogin, adminFetch, getToken } from "@/admin/api";
import { mediaUrl } from "@/lib/mediaUrl";

const AdminLogin = () => {
  const { lang } = useLanguage();
  const t = adminContent[lang].login;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken()) {
      adminFetch("/admin/me")
        .then(() => navigate("/admin/projects", { replace: true }))
        .catch(() => {});
    }
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(email, password);
      navigate("/admin/projects", { replace: true });
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("Invalid credentials")) setError(t.errors.invalid);
      else if (msg.includes("Too many")) setError(t.errors.locked);
      else if (err instanceof TypeError) setError(t.errors.connection);
      else setError(t.errors.generic);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-softwhite px-5" data-testid="admin-login-page">
      <Toaster position="bottom-center" />
      <LanguageSwitcher className="fixed end-5 top-5" />

      <div className="w-full max-w-sm rounded-[1.75rem] border border-line bg-white p-8 shadow-soft">
        <div className="text-center">
          <img src={mediaUrl("/brand/logo-primary.png")} alt="YacoubCreates" className="mx-auto h-7 w-auto" width="374" height="93" />
          <h1 className="font-display mt-6 text-2xl font-bold text-charcoal">{t.title}</h1>
          <p className="mt-1.5 text-sm text-graphite/60">{t.sub}</p>
        </div>

        <form onSubmit={onSubmit} className="mt-7 flex flex-col gap-4" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="mb-1.5 block text-xs font-semibold text-graphite">
              {t.email}
            </label>
            <input
              id="admin-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="h-11 w-full rounded-xl border border-line bg-softwhite px-4 text-sm text-ink outline-none transition-colors focus:border-sage"
              dir="ltr"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1.5 block text-xs font-semibold text-graphite">
              {t.password}
            </label>
            <input
              id="admin-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="h-11 w-full rounded-xl border border-line bg-softwhite px-4 text-sm text-ink outline-none transition-colors focus:border-sage"
              dir="ltr"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-xs font-medium text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-1 h-11 rounded-full bg-charcoal text-sm font-semibold text-softwhite transition-colors hover:bg-graphite disabled:opacity-60"
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
